import { logout, login, spitfire } from '@/core/spitfire';
import { setAccessToken, setLoginData } from '@/features/auth/slice';
import { setSSORegion } from '@/features/SSO/slice';
import { NOTIFY_LOGIN_COOKIE_DOMAIN } from '@/core/constants';

import type { TAppLocales } from '@/app/language/types';
import type { components } from '@/types/generated';

import { Buffer } from 'buffer';

const service = spitfire.injectEndpoints({
  endpoints: (builder) => ({
    login: builder.mutation<components['schemas']['LoginResponse'], LoginRequest>({
      query: ({ email, password, locale, companyName }) => ({
        url: 'auth/login',
        method: 'POST',
        headers: {
          authorization: `Basic ${Buffer.from(`${email}:${password}`).toString('base64')}`,
        },
        body: {
          locale,
          companyName,
        },
        skipRefreshHandling: true,
      }),
      async onCacheEntryAdded(_, { dispatch, cacheDataLoaded }) {
        dispatch(login());
        const { data } = await cacheDataLoaded;
        dispatch(setLoginData(data));
        dispatch(setSSORegion(data.region));
      },
      extraOptions: {
        errorHandlerSkipOptions: {
          skipAll: true,
        },
      },
    }),
    loginWithSession: builder.mutation<
      components['schemas']['LoginResponse'],
      LoginWithSessionRequest
    >({
      query: (body) => ({
        url: 'auth/login_with_session',
        method: 'POST',
        headers: {
          'X-Session': JSON.stringify(body),
        },
        skipRefreshHandling: true,
      }),
      async onCacheEntryAdded(_, { dispatch, cacheDataLoaded }) {
        dispatch(login());
        const { data } = await cacheDataLoaded;
        dispatch(setLoginData(data));
        dispatch(setSSORegion(data.region));
      },
      extraOptions: {
        errorHandlerSkipOptions: {
          skipAll: true,
        },
      },
    }),
    loginWithSignature: builder.mutation<
      components['schemas']['LoginResponse'],
      components['schemas']['LoginWithSignatureRequest']
    >({
      query: (body) => ({
        url: 'auth/login_with_signature',
        method: 'POST',
        body,
        skipRefreshHandling: true,
      }),
      async onCacheEntryAdded(_, { dispatch, cacheDataLoaded }) {
        dispatch(login());
        const { data } = await cacheDataLoaded;
        dispatch(setLoginData(data));
        dispatch(setSSORegion(data.region));
      },
      extraOptions: {
        errorHandlerSkipOptions: {
          skipAll: true,
        },
      },
    }),
    loginAsUser: builder.mutation<
      components['schemas']['LoginResponse'],
      components['schemas']['LoginAsUserRequest']
    >({
      query: (body) => ({
        url: 'auth/login_as_user',
        method: 'POST',
        body,
        skipRefreshHandling: true,
      }),
      async onCacheEntryAdded(_, { dispatch, cacheDataLoaded }) {
        const { data } = await cacheDataLoaded;
        dispatch(logout());
        dispatch(setLoginData(data));
        dispatch(setSSORegion(data.region));
      },
    }),
    notifyLogin: builder.mutation<components['schemas']['NotifyLoginResponse'], void>({
      query: () => ({ url: 'auth/notify_login', method: 'GET' }),
      async onCacheEntryAdded(_, { cacheDataLoaded }) {
        const {
          data: { cookieName, cookieValue },
        } = await cacheDataLoaded;
        document.cookie = `${cookieName}=${cookieValue};path=/;domain=${NOTIFY_LOGIN_COOKIE_DOMAIN}`;
        window?.gigya?.accounts.getAccountInfo();
      },
    }),
    checkSSO: builder.mutation<
      components['schemas']['CheckSSOResponse'],
      components['schemas']['CheckSSORequest']
    >({
      query: (body) => ({
        url: 'auth/check_sso',
        method: 'POST',
        body,
        skipRefreshHandling: true,
      }),
      extraOptions: {
        errorHandlerSkipOptions: {
          skipAll: true,
        },
      },
    }),
    getCompanies: builder.query<components['schemas']['GetCompaniesResponse'], void>({
      query: () => ({
        url: 'auth/get_companies',
      }),
    }),
    selectCompany: builder.mutation<
      components['schemas']['GeneratedTokenResponse'],
      SelectCompanyRequest
    >({
      query: ({ isMocked, loginToken, companyId }) => ({
        url: 'auth/select_company',
        method: 'POST',
        body: { isMocked, loginToken, companyId },
        skipRefreshHandling: true,
      }),
      invalidatesTags: ['CompanyLogo'],
      extraOptions: {
        errorHandlerSkipOptions: {
          skipAll: true,
        },
      },
      async onCacheEntryAdded(_, { dispatch, cacheDataLoaded }) {
        const { data } = await cacheDataLoaded;
        dispatch(
          setAccessToken({ accessToken: data.accessToken, refreshTokenExp: data.refreshTokenExp }),
        );
      },
    }),

    signUp: builder.mutation<
      components['schemas']['StatusResponse'],
      components['schemas']['SignUpRequest']
    >({
      query: (body) => ({
        url: '/accounts/sign_up',
        method: 'POST',
        body,
        skipRefreshHandling: true,
      }),
    }),

    checkRegistrationToken: builder.query<
      components['schemas']['StatusResponse'],
      components['schemas']['CheckRegistrationTokenRequest']
    >({
      query: (body) => ({
        url: '/accounts/check_registration_token',
        method: 'POST',
        body,
        skipRefreshHandling: true,
      }),
      extraOptions: {
        errorHandlerSkipOptions: {
          skipAll: true,
        },
      },
    }),

    register: builder.mutation<
      components['schemas']['StatusResponse'],
      components['schemas']['RegisterRequest']
    >({
      query: (body) => ({
        url: '/accounts/register',
        method: 'POST',
        body,
        skipRefreshHandling: true,
      }),
      extraOptions: {
        errorHandlerSkipOptions: {
          skipByStatusCodes: [403],
        },
      },
    }),

    changeCompany: builder.mutation<
      components['schemas']['GeneratedTokenResponse'],
      components['schemas']['ChangeCompanyRequest']
    >({
      query: ({ companyId }) => ({
        url: 'auth/change_company',
        method: 'POST',
        body: { companyId },
      }),
      async onCacheEntryAdded(_, { dispatch, cacheDataLoaded }) {
        const { data } = await cacheDataLoaded;
        dispatch(
          setAccessToken({ accessToken: data.accessToken, refreshTokenExp: data.refreshTokenExp }),
        );
        // to invalidate after setAccessToken to avoid refreshing token again
        dispatch(
          service.util.invalidateTags([
            'GetSSOConfig',
            'UserSettings',
            'TravelPolicies',
            'HyattSettings',
            'CompanyLogo',
            'TravelerInfo',
            'Flags',
          ]),
        );
      },
    }),
    logout: builder.mutation<components['schemas']['StatusResponse'], void>({
      query: () => ({ url: 'auth/logout', method: 'GET' }),
      async onCacheEntryAdded(_, { dispatch, cacheDataLoaded }) {
        await cacheDataLoaded;
        dispatch(logout());
      },
    }),
    refreshToken: builder.mutation<RefreshTokenResult, void>({
      query: () => ({
        method: 'GET',
        url: 'auth/refresh',
      }),
      async onCacheEntryAdded(_, { dispatch, cacheDataLoaded }) {
        const { data } = await cacheDataLoaded;
        dispatch(
          setAccessToken({ accessToken: data.accessToken, refreshTokenExp: data.refreshTokenExp }),
        );
      },
    }),
    getLoginPageAssets: builder.query<
      components['schemas']['LoginPageAssetsResponse'],
      components['schemas']['LoginPageAssetsRequest']
    >({
      query: (body) => ({
        url: '/login_page_assets',
        method: 'POST',
        body,
        skipRefreshHandling: true,
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useLoginWithSessionMutation,
  useLogoutMutation,
  useNotifyLoginMutation,
  useSelectCompanyMutation,
  useGetCompaniesQuery,
  useChangeCompanyMutation,
  useRefreshTokenMutation,
  useGetLoginPageAssetsQuery,
  useSignUpMutation,
  useCheckRegistrationTokenQuery,
  useRegisterMutation,
  useCheckSSOMutation,
  useLoginWithSignatureMutation,
  useLoginAsUserMutation,
} = service;

export type UserCompany = components['schemas']['Company'] & {
  isMocked?: boolean;
};

export type LoginRequest = {
  email: string;
  password: string;
  locale?: TAppLocales;
  companyName?: string;
};

export const LoginWithSessionQuery = {
  signature: 'signature',
  loginToken: 'loginToken',
  timestamp: 'timestamp',
} as const;

export type LoginWithSessionRequest = {
  signature: string;
  timestamp: number;
  cookieValue: string;
};

export type RefreshTokenResult = components['schemas']['GeneratedTokenResponse'];

type SelectCompanyRequest = components['schemas']['SelectCompanyRequest'] & {
  isMocked?: boolean;
};
